import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { answerBlock } from 'actions';

import QuestionnaireType from 'common/questionnaires/QuestionnaireType';
import Question from 'components/Questionnaires/Question';
import QuestionBlock from 'components/Questionnaires/QuestionBlock';
import { QuestionTextField, QuestionDropDownField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion, isBlockFullyValid, isEmptyObject } from 'components/Questionnaires/validators';
import { QuestionAddressBlock, QuestionGiinBlock } from 'components/Questionnaires/blocks';

export const QuestionDreBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block?.answers ?? {});

  const qAnswers = useSelector(state => state?.questionnaires?.formQuestionnaire?.answers);

  const getRequiredValidator = message => ({
    conditions: [
      { condition: requiredValidator, message }
    ]
  });

  const showBlock = (b) => {
    if (b) {
      if (Object.prototype.hasOwnProperty.call(b, 'visible')) {
        if (typeof b.visible === 'boolean') {
          return b.visible;
        }
        if (typeof b.visible === 'function') {
          return b.visible(qAnswers);
        }
      }
    }
    return false;
  };

  const isBlockOptional = () => {
    let isOptional = false;
    if (block?.componentProps && Object.prototype.hasOwnProperty.call(block?.componentProps, 'optional')) {
      if (typeof block.componentProps.optional === 'boolean') {
        isOptional = !!block.componentProps.optional;
      }
      if (typeof block.componentProps.optional === 'function') {
        isOptional = !!block.componentProps.optional(block.answers, qAnswers);
      }
    }

    return isOptional;
  };

  const defaultNameState = a => ({
    id: 'name',
    component: QuestionTextField,
    label: block?.componentProps?.dreNameLabel,
    disabled: block.disabled ?? undefined,
    validation: isBlockOptional() !== true ? {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.disregardedEntity.dreName.errorMessage' }
      ]
    } : undefined,
    answer: a,
    visible: true,
  });
  const [name, setName] = useState(defaultNameState(answer?.name));

  const defaultTypeState = a => ({
    id: 'type',
    component: QuestionDropDownField,
    label: block?.componentProps?.dreTypeLabel,
    disabled: block.disabled ?? undefined,
    answer: a,
    componentProps: {
      items: 'questionnaire.disregardedEntity.type.items',
      preventTranslation: true
    },
    info: {
      instructions: 'questionnaire.disregardedEntity.type.info'
    },
    visible: true,
  });
  const [type, setType] = useState(defaultTypeState(answer?.type));

  const isGiinVisible = (dreTypeAnswer) => {
    const value = dreTypeAnswer && dreTypeAnswer !== '1' && dreTypeAnswer !== '';
    return !!value;
  };

  const defaultGiinState = () => ({
    id: 'giin',
    component: QuestionGiinBlock,
    componentProps: {
      giinInstructions: 'questionnaire.disregardedEntity.giin.info'
    },
    expandCollapse: false,
    className: 'pl-3 pr-3 pt-0 pb-0 mt-0 no-border nested',
    visible: () => isGiinVisible(),
  });
  const [giin, setGiin] = useState(defaultGiinState());

  const defaultAddressState = () => ({
    id: 'address',
    title: 'questionnaire.address',
    component: QuestionAddressBlock,
    className: 'p-0 no-border nested',
    info: {
      instructions: 'questionnaire.address.1.info'
    },
    visible: true,
    componentProps: {
      optional: isBlockOptional(),
      collapsible: true,
      line1footnote: 'questionnaire.disregardedEntity.address.1.footnote'
    },
    isCollapsed: false
  });
  const [address, setAddress] = useState(defaultAddressState());

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const updateNameValidators = (q, b, a) => {
    if (isBlockOptional() !== true) {
      if (q?.id === 'name') {
        if (q.answer?.length > 0) {
          q.isValid = undefined;
          q.errorMessages = undefined;
        }
        return q;
      }
      return name;
    }

    let nameEntered = answer?.name?.length > 0;
    let typeEntered = answer?.type?.length > 0;
    let giinValueEntered = answer?.giin?.value?.length > 0;
    let giinAppliedForEntered = answer?.giin?.appliedFor?.length > 0;
    let line1Entered = answer?.address?.line1?.length > 0;
    let countryEntered = answer?.address?.country?.length > 0;
    let optionaLinesEntered = answer?.address?.line2?.length > 0 || answer?.address?.line3?.length > 0 || answer?.address?.line4?.length > 0 || answer?.address?.postalCode?.length > 0;
    let typeAnswer = answer?.type;

    switch (q?.id) {
      case 'name':
        nameEntered = q.answer?.length > 0;
        break;
      case 'type':
        typeEntered = q.answer?.length > 0;
        typeAnswer = q.answer;
        break;
      default:
        break;
    }

    if (b && a) {
      switch (b?.id) {
        case 'giin':
          giinValueEntered = a.value?.length > 0;
          giinAppliedForEntered = a.appliedFor?.length > 0;
          break;
        case 'address':
          line1Entered = a.line1?.length > 0;
          countryEntered = a.country?.length > 0;
          optionaLinesEntered = a.line2?.length > 0 || a.line3?.length > 0 || a.line4?.length > 0 || a.postalCode?.length > 0;
          break;
        default:
          break;
      }
    }

    if (!isGiinVisible(typeAnswer)) {
      giinValueEntered = false;
      giinAppliedForEntered = false;
    }

    const newName = q?.id === 'name' ? q : { ...name };

    if (nameEntered || typeEntered || giinValueEntered || giinAppliedForEntered || line1Entered || countryEntered || optionaLinesEntered) {
      newName.validation = newName.validation ?? getRequiredValidator('questionnaire.disregardedEntity.dreName.errorMessage');
    } else {
      newName.validation = undefined;
      newName.isValid = undefined;
      newName.errorMessages = undefined;
    }

    switch (q?.id) {
      case 'name':
        q.validation = newName.validation;
        q.isValid = newName.isValid;
        q.errorMessages = newName.errorMessages;
        break;
      default:
        if (
          // b.isForceEdited && b.isForceEdited === true && q.isForceEdited && q.isForceEdited === true && q.isValid && q.isValid !== undefined
          //  &&
          (newName.isForceEdited === undefined || newName.isForceEdited !== true || newName.answer === undefined || newName.isValid === undefined)
          && ((q !== null && q !== undefined && q.isForceEdited) || (q !== null && q !== undefined && q.isForceEdited))) {
          newName.isForceEdited = true;
          newName.answer = '';
          setName(newName);
          validateQuestion(newName);
        } else {
          setName(newName);
        }
        break;
    }

    return newName;
  };

  const onQuestionChange = (q, value, setAns = true) => {
    switch (q.id) {
      case 'name': {
        let newName = { ...q, answer: value };
        newName = updateNameValidators(newName);
        validateQuestion(newName);
        setName(newName);
        if (setAns) {
          setAnswer({ ...answer, name: value });
        }
        break;
      }
      case 'type': {
        const newType = { ...q, answer: value };
        updateNameValidators(newType);
        validateQuestion(newType);
        setType(newType);
        if (value) {
          setAddress({ ...address, componentProps: { ...address.componentProps, optional: false } });
        } else {
          const newAddress = { ...address, componentProps: { ...address.componentProps, optional: isBlockOptional() } };
          if (isBlockOptional()) {
            const line1Entered = answer?.address?.line1?.length > 0;
            const countryEntered = answer?.address?.country?.length > 0;
            const optionaLinesEntered = answer?.address?.line2?.length > 0 || answer?.address?.line3?.length > 0 || answer?.address?.line4?.length > 0 || answer?.address?.postalCode?.length > 0;
            if (!line1Entered && !countryEntered && !optionaLinesEntered) {
              newAddress.isValid = undefined;
              newAddress.errorMessages = undefined;
            }
          }
          setAddress(newAddress);
        }
        const giinVisible = isGiinVisible(value);
        if (setAns) {
          setAnswer({ ...answer, type: value, giin: giinVisible ? answer.giin : undefined });
        }
        const giinChanges = {};
        if (giinVisible) {
          giinChanges.visible = true;
        } else {
          giinChanges.answers = undefined;
          giinChanges.isValid = undefined;
          giinChanges.visible = false;
        }
        setGiin({ ...giin, ...giinChanges });
        break;
      }
      default:
        break;
    }
  };

  const onBlockAnswer = (b, a, v) => {
    switch (b.id) {
      case 'address': {
        const newBlock = { ...b, ...{ answers: a, isValid: v } };
        updateNameValidators(null, newBlock, a);
        setAddress(newBlock);
        setAnswer({ ...answer, address: a });
        break;
      }
      case 'giin': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        updateNameValidators(null, newBlock, a);
        setGiin(newBlock);
        setAnswer({ ...answer, giin: a });
        break;
      }
      default:
        break;
    }
  };

  const isBlockValid = () => {
    let forceValidation = false;
    if (typeof block.linkBlock === 'boolean') {
      forceValidation = block.linkBlock !== true;
    }
    if (typeof block.linkBlock === 'function') {
      forceValidation = block.linkBlock(block.answers, qAnswers) === false;
    }

    if (forceValidation) {
      const newNameValidation = { ...name.validation, ...getRequiredValidator('questionnaire.disregardedEntity.dreName.errorMessage') };
      const newName = { ...name, ...newNameValidation };
      setName(newName);
    }

    const subBlocks = [address];
    if (isGiinVisible(type.answer)) {
      subBlocks.push(giin);
    }
    const questions = [name, type];

    if (questions.some(x => x.isForceEdited === true)) {
      const qsToCorrect = questions.filter(x => x.isForceEdited !== true || x.answer === undefined);
      qsToCorrect.forEach((correctedItem) => {
        if (correctedItem.answer === undefined) {
          correctedItem.answer = '';
          correctedItem.isForceEdited = true;
        }

        if (correctedItem.answer === '' && correctedItem.isForceEdited !== true) {
          correctedItem.isForceEdited = true;
        }

        const index = questions.findIndex(originalItem => originalItem === correctedItem);
        if (index !== -1) {
          questions[index] = correctedItem;
        }
      });
    }

    return isBlockFullyValid(subBlocks, questions, block?.answers, qAnswers);
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isBlockValid()));
    } else {
      onAnswer(block, answer, isBlockValid());
    }
  }, [answer]);

  useEffect(() => {
    const nameMismatch = name.answer !== block.answers?.name;
    const typeMismatch = type.answer !== block.answers?.type;
    const addressMismatch = address.answers !== block.answers?.address;
    if ((nameMismatch && !block.answers?.name)
      && (typeMismatch && !block.answers?.type)
      && (addressMismatch && (!block.answers?.address || isEmptyObject(block.answers?.address)))
    ) {
      setName(defaultNameState(undefined));
      setType(defaultTypeState(undefined));
      setAddress(defaultAddressState());
      setGiin(defaultGiinState());
      setAnswer(block.answers ?? {});
      return;
    }

    if (nameMismatch) {
      if (block.answers?.name === undefined) {
        let newName = { ...name };
        newName.answer = undefined;
        newName = updateNameValidators(newName);
        newName.isForceEdited = false;
        setName(newName);
      } else {
        onQuestionChange(name, block.answers?.name);
      }
    }
    if (typeMismatch) {
      if (block.answers?.type === undefined) {
        const newType = { ...type };
        newType.isValid = undefined;
        newType.isForceEdited = undefined;
        newType.validation = undefined;
        newType.errorMessages = undefined;
        newType.answer = undefined;
        setType(newType);
        const giinChanges = {};
        giinChanges.answers = undefined;
        giinChanges.isValid = undefined;
        giinChanges.visible = false;
        setGiin({ ...giin, ...giinChanges });
      } else {
        onQuestionChange(type, block.answers?.type);
      }
    }
    if (addressMismatch) {
      const newBlock = { ...address, ...{ answers: block.answers.address ?? {} } };
      setAddress(newBlock);
    }
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      const newAnswer = { ...answer };
      if (!answer?.name) {
        onQuestionChange({ ...name, isForceEdited: true }, '', false);
        newAnswer.name = '';
      }
      if (!answer?.type) {
        onQuestionChange({ ...type, isForceEdited: true }, '', false);
        newAnswer.type = '';
      }
      setAnswer({ ...newAnswer });
      if (!answer?.giin || isEmptyObject(answer?.giin)) {
        const newBlock = { ...giin, ...{ isForceEdited: true } };
        setGiin(newBlock);
      }
      if (!answer?.address || isEmptyObject(answer?.address) || !answer?.address?.line1 || !answer?.address?.country) {
        const newBlock = { ...address, ...{ isForceEdited: true } };
        setAddress(newBlock);
      }
    } else {
      if (giin.isForceEdited !== block.isForceEdited) {
        const newBlock = { ...giin, ...{ isForceEdited: block.isForceEdited } };
        setGiin(newBlock);
      }
      if (address.isForceEdited !== block.isForceEdited) {
        const newBlock = { ...address, ...{ isForceEdited: block.isForceEdited } };
        setAddress(newBlock);
      }
      if (name.isForceEdited !== block.isForceEdited) {
        setName({ ...name, ...{ isForceEdited: block.isForceEdited } });
      }
      if (type.isForceEdited !== block.isForceEdited) {
        setType({ ...type, isForceEdited: block.isForceEdited });
      }
    }
  }, [block.isForceEdited]);

  useEffect(() => {
    if (isBlockOptional() !== true) {
      const newNameValidation = { ...name.validation, ...getRequiredValidator('questionnaire.disregardedEntity.dreName.errorMessage') };
      const newName = { ...name, validation: newNameValidation };
      setName(newName);
      setAddress({ ...address, componentProps: { ...address.componentProps, optional: false } });
    } else {
      updateNameValidators();
      if ((!answer?.giin || isEmptyObject(answer?.giin)) && giin.isValid === false) {
        const giinChanges = { ...giin };
        giinChanges.answers = undefined;
        giinChanges.isValid = undefined;
        setGiin(giinChanges);
      }
      if ((!answer?.address || isEmptyObject(answer?.address) || !answer?.address?.line1 || !answer?.address?.country) && address.isValid === false) {
        const addressChanges = { ...address };
        addressChanges.answers = undefined;
        addressChanges.isValid = undefined;
        setAddress(addressChanges);
      }
    }
  }, [isBlockOptional()]);

  return (<React.Fragment>
    <Question
      key="name"
      blockId={ blockIdentifier }
      question={ name }
      { ...name.componentProps }
      onChange={ (q, a) => onQuestionChange(q, a) }
    />
    <Question
      key="type"
      blockId={ blockIdentifier }
      question={ type }
      { ...type.componentProps }
      onChange={ (q, a) => onQuestionChange(q, a) }
    />
    { showBlock(giin)
      && <QuestionBlock
        questionnaireType={ QuestionnaireType.Form }
        key={ `${ block.id }-${ giin.id }` }
        block={ giin }
        onAnswer={ onBlockAnswer }
      /> }
    { showBlock(address) && <div className="col-12">
      <QuestionBlock
        questionnaireType={ QuestionnaireType.Form }
        key={ `${ block.id }-${ address.id }` }
        block={ address }
        onAnswer={ onBlockAnswer }
      />
    </div> }
  </React.Fragment>);
};

QuestionDreBlock.displayName = 'QuestionDreBlock';

QuestionDreBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
};

QuestionDreBlock.defaultProps = {
  onAnswer: null,
};

export default QuestionDreBlock;
